import type { ReactNode } from 'react';

// Steam-style avatar frames: decorative SVG overlays that extend past the
// avatar box (corner ornaments, circuit stubs, filigree). Self-contained like
// Icon.tsx — no image assets. The avatar square spans 0..100 in the viewBox;
// everything outside that range hangs over the edge, so the host element must
// be `overflow: visible` (see the .frame-* rules in theme.css).

/** One corner's artwork repeated at 0/90/180/270° around the avatar centre. */
function corners(node: ReactNode) {
  return [0, 90, 180, 270].map((r) => (
    <g key={r} transform={`rotate(${r} 50 50)`}>
      {node}
    </g>
  ));
}

const cyanCorner = (
  <>
    {/* bracket hugging the corner */}
    <path d="M -7 24 L -7 8 Q -7 -7 8 -7 L 24 -7" fill="none" strokeWidth="3.5" />
    {/* circuit stub running off the top edge into a node */}
    <path d="M 34 -7 v -6" fill="none" strokeWidth="2.5" />
    <circle cx="34" cy="-17" r="3" fill="currentColor" stroke="none" />
    {/* chip pad on the corner itself */}
    <rect x="-11" y="-11" width="8" height="8" rx="2" fill="#04101a" strokeWidth="2.5" />
  </>
);

const magentaCorner = (
  <>
    {/* twin energy arcs */}
    <path d="M -9 30 Q -9 -9 30 -9" fill="none" strokeWidth="3.5" />
    <path d="M -14 24 Q -14 -14 24 -14" fill="none" strokeWidth="2" opacity="0.55" />
    {/* spark tick shooting out of the corner diagonal */}
    <path d="M -13 -13 l -6 -6" fill="none" strokeWidth="3" />
  </>
);

const goldCorner = (
  <>
    {/* bracket hugging the corner, same weight as the cyan/magenta brackets */}
    <path d="M -8 22 L -8 8 Q -8 -8 8 -8 L 22 -8" fill="none" strokeWidth="3.5" />
    {/* jewel stud set into the corner tip */}
    <rect x="-6" y="-6" width="7" height="7" rx="1.5" fill="currentColor" stroke="none" transform="rotate(45 -2.5 -2.5)" />
  </>
);

const FRAME_ART: Record<string, { color: string; art: ReactNode }> = {
  cyan: {
    color: '#00e5ff',
    art: (
    <g stroke="#00e5ff" color="#00e5ff" strokeLinecap="round">
      {corners(cyanCorner)}
      {/* data ring flowing around the border */}
      <rect
        x="-2" y="-2" width="104" height="104" rx="18"
        fill="none" strokeWidth="1.6" opacity="0.7" className="af-flow"
      />
    </g>
    ),
  },
  magenta: {
    color: '#ff2e88',
    art: (
    <g stroke="#ff2e88" color="#ff2e88" strokeLinecap="round">
      {corners(magentaCorner)}
      <rect
        x="-3" y="-3" width="106" height="106" rx="19"
        fill="none" strokeWidth="1.8" opacity="0.75" className="af-flow af-fast"
      />
      {/* heartbeat blip under the bottom edge */}
      <path
        d="M 24 111 h 14 l 5 -9 l 7 16 l 5 -7 h 21"
        fill="none" strokeWidth="2.5" className="af-beat"
      />
    </g>
    ),
  },
  gold: {
    color: '#ffd700',
    art: (
    <g stroke="#ffd700" color="#ffd700" strokeLinecap="round">
      {corners(goldCorner)}
      {/* ceremonial border, same single-ring language as cyan/magenta */}
      <rect
        x="-2" y="-2" width="104" height="104" rx="18"
        fill="none" strokeWidth="1.6" opacity="0.75" className="af-shimmer"
      />
      {/* crown gem at the top centre */}
      <path d="M 50 -16 l 8 9 l -8 9 l -8 -9 Z" fill="#ffd700" stroke="#fff3b0" strokeWidth="1.5" />
      <circle cx="34" cy="-8" r="2.2" fill="#fff3b0" stroke="none" />
      <circle cx="66" cy="-8" r="2.2" fill="#fff3b0" stroke="none" />
    </g>
    ),
  },
};

/**
 * Overlay for a shop-bought avatar frame; renders nothing for unknown/absent
 * ids. Drop inside any avatar box that has the matching `frame-*` class.
 */
export function AvatarFrame({ frame }: { frame?: string | null }) {
  const def = frame ? FRAME_ART[frame] : undefined;
  if (!def) return null;
  return (
    // `color` drives the drop-shadow glow (currentColor in the CSS filter),
    // independent of the avatar's role-coloured text colour.
    <svg className="avatar-frame" viewBox="-14 -14 128 128" style={{ color: def.color }} aria-hidden>
      {def.art}
    </svg>
  );
}
